import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Meals from "@/pages/Meals";
import Movement from "@/pages/Movement";
import Coaching from "@/pages/Coaching";
import Measurements from "@/pages/Measurements";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminComments from "@/pages/AdminComments";
import AdminWaitlist from "@/pages/AdminWaitlist";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Waitlist from "@/pages/Waitlist";
import AuthCallback from "@/pages/AuthCallback";
import "@/App.css";

function AppLayout({ children }) {
  return <Layout>{children}</Layout>;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (!user || user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Detect Emergent OAuth callback in URL fragment BEFORE any protected-route check
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/waitlist" element={<Waitlist />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/onboarding" element={<ProtectedRoute requireOnboarding={false}><Onboarding /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />

      <Route path="/app" element={<ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/app/meals" element={<ProtectedRoute><AppLayout><Meals /></AppLayout></ProtectedRoute>} />
      <Route path="/app/movement" element={<ProtectedRoute><AppLayout><Movement /></AppLayout></ProtectedRoute>} />
      <Route path="/app/coaching" element={<ProtectedRoute><AppLayout><Coaching /></AppLayout></ProtectedRoute>} />
      <Route path="/app/measurements" element={<ProtectedRoute><AppLayout><Measurements /></AppLayout></ProtectedRoute>} />

      <Route path="/admin" element={<ProtectedRoute><AdminOnly><AppLayout><AdminDashboard /></AppLayout></AdminOnly></ProtectedRoute>} />
      <Route path="/admin/comments" element={<ProtectedRoute><AdminOnly><AppLayout><AdminComments /></AppLayout></AdminOnly></ProtectedRoute>} />
      <Route path="/admin/waitlist" element={<ProtectedRoute><AdminOnly><AppLayout><AdminWaitlist /></AppLayout></AdminOnly></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
